import { APP_V2_APP_EVENTS, isCapacitorPlatform } from "@src/appV2/lib";
import {
  addScreenOrientationListener,
  getScreenOrientation,
  lockScreenOrientation,
  type ScreenOrientationListenerHandle,
  unlockScreenOrientation,
} from "@src/appV2/NativeAdapters/screenOrientation.adapter";
import { useCallback, useEffect, useState } from "react";

import { logError } from "../lib/analytics/log";

// Must be a subset of OrientationLockType from @capacitor/screen-orientation
export enum LockScreenOrientationVariant {
  NATURAL = "natural",
  PORTRAIT_PRIMARY = "portrait-primary",
  LANDSCAPE_PRIMARY = "landscape-primary",
  PORTRAIT_SECONDARY = "portrait-secondary",
  LANDSCAPE_SECONDARY = "landscape-secondary",
}

export interface UseScreenOrientation {
  orientation: OrientationType | undefined;
  lockOrientation: (orientation: LockScreenOrientationVariant) => Promise<void>;
  unlockOrientation: () => Promise<void>;
}

/**
 * @deprecated
 * Screen locking behavior should be avoided across the app. This hook
 * is on path to be removed, and an alternative solution will be implemented
 * that does not depend on locking the screen orientation.
 *
 * TODO: Add test coverage for this hook
 */
export function useDeprecatedScreenOrientation(): UseScreenOrientation {
  const [orientation, setOrientation] = useState<OrientationType>();
  const isPlatformMobile = isCapacitorPlatform();

  useEffect(() => {
    if (!isPlatformMobile) {
      return () => null;
    }

    let listenerHandle: ScreenOrientationListenerHandle | undefined;

    void (async () => {
      const currentOrientation = await getScreenOrientation();
      if (currentOrientation) {
        setOrientation(currentOrientation);
      }

      listenerHandle = await addScreenOrientationListener((orientationType) => {
        setOrientation(orientationType);
      });
    })();

    return () => {
      void listenerHandle?.remove();
    };
  }, [isPlatformMobile]);

  const lockOrientation = useCallback(
    async (orientation: LockScreenOrientationVariant) => {
      if (!isPlatformMobile) {
        return;
      }

      // unlock the orientation first in case it was previously locked
      await unlockScreenOrientation().catch((error: unknown) => {
        /* locks not supported, no action */
        logError(APP_V2_APP_EVENTS.SCREEN_ORIENTATION_LOCK_FAILED, {
          error,
          metadata: { operation: "unlock" },
        });
      });

      // lock the orientation to the desired orientation
      await lockScreenOrientation(orientation).catch((error: unknown) => {
        /* locks not supported, no action */
        logError(APP_V2_APP_EVENTS.SCREEN_ORIENTATION_LOCK_FAILED, {
          error,
          metadata: { operation: "lock" },
        });
      });
    },
    [isPlatformMobile]
  );

  const unlockOrientation = useCallback(async () => {
    if (!isPlatformMobile) {
      return;
    }

    // unlock the orientation first
    await unlockScreenOrientation().catch((error: unknown) => {
      /* locks not supported, no action */
      logError(APP_V2_APP_EVENTS.SCREEN_ORIENTATION_LOCK_FAILED, {
        error,
        metadata: { operation: "unlock" },
      });
    });

    // By default, we want to forcefully lock the screen orientation to portrait
    await lockScreenOrientation(LockScreenOrientationVariant.PORTRAIT_PRIMARY).catch(
      (error: unknown) => {
        /* locks not supported, no action */
        logError(APP_V2_APP_EVENTS.SCREEN_ORIENTATION_LOCK_FAILED, {
          error,
          metadata: { operation: "lock" },
        });
      }
    );
  }, [isPlatformMobile]);

  return {
    orientation,
    lockOrientation,
    unlockOrientation,
  };
}
